import React, { useEffect, useMemo, useState } from 'react'
import { FieldControl } from 'spark-core-dx/components';
import { useCoreContext } from 'spark-core-dx/contexts';
import { useGenericManager } from 'spark-core-dx/hooks';
import { SystemTypes } from 'spark-core-dx/services';
import { useDashboardDomainContext } from '../../../DashboardDomainProvider';
import { useVisualFormContext } from '../../../contexts/VisualFormContext';
import { ItemServiceFactory } from '../../../../../services/ItemServiceFactory';

export const Icon = () => {
    const { iconSelectData } = useDashboardDomainContext();
    const { visualObject, setVisualObject } = useVisualFormContext();
    const coreContext = useCoreContext();
    const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
    const iconMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Icon);
    const localIconSelectData = iconMgr?.selectData?.find((x) => x.FieldName === "Icon")?.jsonData;
    const [queriedIconOptions, setQueriedIconOptions] = useState([]);
    const normalizeIconOptions = (items) => {
        if (!Array.isArray(items)) {
            return [];
        }

        return items
            .map((item) => {
                const value = item?.Value ?? item?.value ?? item?.Key ?? item?.Name ?? item?._id;
                const text = item?.Text ?? item?.text ?? item?.DisplayName ?? item?.Name ?? item?.Key ?? value;

                if (!value || !text) {
                    return null;
                }

                return {
                    Text: text,
                    Value: value,
                    Icon: item?.Icon ?? value,
                    Disabled: item?.Disabled === true,
                    Important: item?.Important === true,
                    Sequence: typeof item?.Sequence === "number" ? item.Sequence : -1,
                };
            })
            .filter(Boolean)
            .sort((a, b) => String(a?.Text ?? "").localeCompare(String(b?.Text ?? "")));
    };
    const coreIconOptions = useMemo(() => {
        const icons = coreContext?.Icons;
        if (!icons || typeof icons !== "object") {
            return [];
        }

        return Object.entries(icons)
            .map(([key, icon]) => ({
                Text: icon?.DisplayName ?? icon?.Name ?? key,
                Value: key,
                Icon: key,
                Disabled: icon?.Disabled === true,
                Important: icon?.Important === true,
                Sequence: typeof icon?.Sequence === "number" ? icon.Sequence : -1,
            }))
            .sort((a, b) => String(a?.Text ?? "").localeCompare(String(b?.Text ?? "")));
    }, [coreContext?.Icons]);
    const resolvedIconSelectData =
        (normalizeIconOptions(iconSelectData).length > 0 ? normalizeIconOptions(iconSelectData) : null) ??
        (normalizeIconOptions(localIconSelectData).length > 0 ? normalizeIconOptions(localIconSelectData) : null) ??
        (coreIconOptions.length > 0 ? coreIconOptions : null) ??
        queriedIconOptions;

    useEffect(() => {
        if (resolvedIconSelectData && resolvedIconSelectData.length > 0) {
            return;
        }

        iconMgr?.GetSelectData?.({
            DisplayName: "Icon",
            DataOperationItem: "Icon",
            FieldName: "Icon",
        });
    }, [iconMgr, resolvedIconSelectData]);

    useEffect(() => {
        let alive = true;

        const loadIcons = async () => {
            if ((resolvedIconSelectData?.length ?? 0) > 0) {
                return;
            }

            const result = await iconMgr?.Query?.({
                model: "x => x.IsActive == true && x.IsDeleted == false",
            });

            if (!alive) {
                return;
            }

            const nextOptions = normalizeIconOptions(result?.Items ?? []);
            if (nextOptions.length > 0) {
                setQueriedIconOptions(nextOptions);
            }
        };

        void loadIcons();

        return () => {
            alive = false;
        };
    }, [iconMgr, resolvedIconSelectData]);

    return (
        <div className="visual-icon-field">
            <FieldControl
                fieldMetadata={{
                    FieldType: SystemTypes.UIFieldType.IconSelect,
                    FieldName: "Icon",
                    DisplayName: "Icon",
                    DataJson: resolvedIconSelectData
                }}
                groupId={"visual"}
                value={visualObject?.Icon ?? visualObject?.icon}
                onChange={(v) => {
                    setVisualObject(prev => ({
                        ...prev,
                        Icon: v,
                    }));
                }}
            />
        </div>
    )
}
